<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div v-if="website" class="mb-4">
          <a :href="`/businesses/${businessId}/websites`" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to Websites
          </a>
        </div>
        <div v-if="website" class="flex items-start justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ website.name }}
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ website.domain }}
            </p>
          </div>
          <button
            @click="openEditForm"
            class="btn btn-secondary btn-sm">
            Edit
          </button>
        </div>
      </div>
    </div>

    <div v-if="website" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Left column: Details -->
        <div class="lg:col-span-1 space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Website Details
            </h3>
            <dl class="space-y-3">
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Domain
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ website.domain }}
                </dd>
              </div>
              <div v-if="website.subdomain">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Subdomain
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ website.subdomain }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Status
                </dt>
                <dd>
                  <span :class="statusClass(website.status)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
                    {{ website.status }}
                  </span>
                </dd>
              </div>
              <div v-if="website.sitemap_url">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Sitemap URL
                </dt>
                <dd class="text-sm text-primary-600 dark:text-primary-400 break-all">
                  <a :href="website.sitemap_url" target="_blank" class="hover:underline">{{ website.sitemap_url }}</a>
                </dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Pages
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ website.pages_count || 0 }} total
                  <span v-if="website.published_pages_count" class="text-gray-500 dark:text-gray-400">
                    ({{ website.published_pages_count }} published)
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <!-- Audit Summary -->
          <AuditSummaryCard
            ref="auditSummaryCard"
            :website-id="website.id"
            @bulk-completed="onBulkAuditCompleted" />

          <!-- Analytics Integration -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Analytics Integration</h3>

            <!-- GA4 -->
            <div class="mb-4">
              <dt class="text-xs text-gray-500 dark:text-gray-400 mb-1">Google Analytics (GA4)</dt>
              <dd v-if="website.ga_enabled && website.analytics_property_id">
                <div class="flex items-center justify-between">
                  <div class="flex items-center text-sm text-green-600 dark:text-green-400">
                    <svg class="w-4 h-4 me-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                    <span class="truncate">{{ website.analytics_property_name || 'Connected' }}</span>
                  </div>
                  <button
                    @click="openGaSetup"
                    :disabled="loadingGoogleAccounts"
                    class="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 ms-2 flex-shrink-0">
                    Change
                  </button>
                </div>
              </dd>
              <dd v-else>
                <button
                  @click="openGaSetup"
                  :disabled="loadingGoogleAccounts"
                  class="btn btn-secondary btn-sm w-full">
                  {{ loadingGoogleAccounts ? 'Loading...' : 'Connect GA4' }}
                </button>
              </dd>
            </div>

            <!-- GSC -->
            <div>
              <dt class="text-xs text-gray-500 dark:text-gray-400 mb-1">Search Console (GSC)</dt>
              <dd v-if="website.gsc_enabled && website.search_console_site_id">
                <div class="flex items-center justify-between">
                  <div class="flex items-center text-sm text-green-600 dark:text-green-400">
                    <svg class="w-4 h-4 me-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                    <span class="truncate">{{ website.search_console_site_url || 'Connected' }}</span>
                  </div>
                  <button
                    @click="openGscSetup"
                    :disabled="loadingGoogleAccounts"
                    class="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 ms-2 flex-shrink-0">
                    Change
                  </button>
                </div>
              </dd>
              <dd v-else>
                <button
                  @click="openGscSetup"
                  :disabled="loadingGoogleAccounts"
                  class="btn btn-secondary btn-sm w-full">
                  {{ loadingGoogleAccounts ? 'Loading...' : 'Connect GSC' }}
                </button>
              </dd>
            </div>
          </div>
        </div>

        <!-- Right column: Analytics + Pages -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Analytics Dashboard -->
          <div v-if="website.ga_enabled || website.gsc_enabled" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <AnalyticsDashboard
              :website-id="website.id"
              :ga-enabled="website.ga_enabled"
              :gsc-enabled="website.gsc_enabled"
              @date-range-changed="onDateRangeChanged" />
          </div>

        </div>
      </div>

      <!-- Tabs -->
      <div class="mt-8">
        <div class="border-b border-gray-200 dark:border-gray-700">
          <nav class="flex gap-6 -mb-px" aria-label="Tabs">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              @click="activeTab = tab.key"
              :class="activeTab === tab.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
              class="py-3 px-1 border-b-2 text-sm font-medium whitespace-nowrap"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab: Pages -->
        <div v-if="activeTab === 'pages'" class="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
              Pages
            </h3>
            <div class="flex space-x-2">
              <button
                @click="computePriorityScores"
                :disabled="computingPriority"
                class="btn btn-secondary btn-sm">
                <svg v-if="computingPriority" class="w-3.5 h-3.5 animate-spin mr-1 inline-block" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ computingPriority ? 'Computing...' : 'Priority Scores' }}
              </button>
              <button
                @click="handleExtractAll"
                :disabled="extractingAll"
                class="btn btn-secondary btn-sm">
                {{ extractingAll ? 'Starting...' : 'Extract All' }}
              </button>
              <button
                @click="openImportSitemap"
                class="btn btn-secondary btn-sm">
                Import Sitemap
              </button>
              <button
                @click="openAddPage"
                class="btn btn-primary btn-sm">
                Add Page
              </button>
            </div>
          </div>
          <PagesList
            ref="pagesList"
            :website-id="website.id"
            :date-range="analyticsDateRange"
            @add-page="openAddPage"
            @view-page="openPageDetail" />
        </div>

        <!-- Tab: Page Builder -->
        <div v-if="activeTab === 'page-builder'" class="mt-6">
          <!-- Template Detail + Builds -->
          <div v-if="selectedBuild">
            <PageBuildDetail
              :build-id="selectedBuild.id"
              @back="selectedBuild = null"
              @updated="onBuildUpdated"
            />
          </div>
          <div v-else-if="selectedTemplate">
            <div class="flex items-center justify-between mb-6">
              <div>
                <button @click="selectedTemplate = null" class="text-sm text-gray-500 hover:text-gray-700 mb-2">
                  &larr; Back to Templates
                </button>
                <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {{ selectedTemplate.name }}
                </h2>
              </div>
              <div class="flex gap-2">
                <button @click="showBulkCreate = true" class="btn btn-secondary btn-sm">
                  Bulk Create
                </button>
              </div>
            </div>
            <PageBuildsList
              ref="buildsList"
              :page-template-id="selectedTemplate.id"
              @select-build="selectedBuild = $event"
              @create-build="showBulkCreate = true"
              @approve-build="handleApproveBuild"
              @reject-build="handleRejectBuild"
              @publish-build="handlePublishBuild"
            />

            <!-- Bulk Create Panel -->
            <UiSlidePanel v-if="showBulkCreate" title="Bulk Create Pages" @close="showBulkCreate = false">
              <BulkCreatePages
                :page-template-id="selectedTemplate.id"
                @cancel="showBulkCreate = false"
                @created="onBulkCreated"
              />
            </UiSlidePanel>
          </div>
          <!-- Templates Grid -->
          <div v-else>
            <TemplatesList
              :website-id="website.id"
              @select-template="selectedTemplate = $event"
            />
          </div>
        </div>

        <!-- Tab: WP Connection -->
        <div v-if="activeTab === 'wp-connection'" class="mt-6">
          <WpConnectionSettings :website-id="website.id" />
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getWebsite, extractAllPages } from '~/javascript/api/websites.js'
import { computePriorityScores as computePriorityScoresApi } from '~/javascript/api/website_pages.js'
import { approvePageBuild, rejectPageBuild, publishPageBuild } from '~/javascript/api/page_builds.js'
import { getGoogleAccounts, getAnalyticsProperties, getSearchConsoleSites } from '~/javascript/api/google_accounts.js'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import WebsiteForm from './form.vue'
import PageForm from './page_form.vue'
import PageDetail from './page_detail.vue'
import SitemapImportForm from './sitemap_import_form.vue'
import AnalyticsSetupForm from './analytics_setup_form.vue'
import AnalyticsDashboard from './analytics_dashboard.vue'
import AuditSummaryCard from './audit_summary_card.vue'
import PagesList from './pages_list.vue'
import TemplatesList from './templates_list.vue'
import PageBuildsList from './page_builds_list.vue'
import PageBuildDetail from './page_build_detail.vue'
import PublishToWpPanel from './publish_to_wp_panel.vue'
import BulkCreatePages from './bulk_create_pages.vue'
import WpConnectionSettings from './wp_connection_settings.vue'

const props = defineProps({
  websiteId: { type: String, required: true },
  businessId: { type: String, required: true }
})

const website = ref(null)
const slidePanel = ref(null)
const pagesList = ref(null)
const buildsList = ref(null)
const extractingAll = ref(false)
const computingPriority = ref(false)
const loadingGoogleAccounts = ref(false)
const auditSummaryCard = ref(null)
const analyticsDateRange = ref(null)

// Tab navigation
const activeTab = ref('pages')
const tabs = [
  { key: 'pages', label: 'Pages' },
  { key: 'page-builder', label: 'Page Builder' },
  { key: 'wp-connection', label: 'WP Connection' }
]

// Page Builder state
const selectedTemplate = ref(null)
const selectedBuild = ref(null)
const showBulkCreate = ref(false)

const onDateRangeChanged = (range) => {
  analyticsDateRange.value = range
}

const fetchWebsite = async() => {
  try {
    const response = await getWebsite(props.websiteId)
    website.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching website:', error)
  }
}

const statusClass = (status) => {
  const map = {
    draft: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  return map[status] || ''
}

const onWebsiteSaved = () => {
  slidePanel.value.close()
  fetchWebsite()
}

const openEditForm = () => {
  slidePanel.value.open({
    component: WebsiteForm,
    props: { website: website.value, businessId: props.businessId },
    events: { saved: onWebsiteSaved }
  })
}

const onPageSaved = () => {
  slidePanel.value.close()
  fetchWebsite()
  pagesList.value?.refresh()
}

const openEditPage = (page) => {
  slidePanel.value.open({
    component: PageForm,
    props: { page, websiteId: website.value.id },
    events: { saved: onPageSaved }
  })
}

const openAddPage = () => {
  slidePanel.value.open({
    component: PageForm,
    props: { websiteId: website.value.id },
    events: { saved: onPageSaved }
  })
}

const openPageDetail = (page) => {
  slidePanel.value.open({
    component: PageDetail,
    props: { page },
    events: {
      edit: (p) => openEditPage(p),
      updated: () => pagesList.value?.refresh(),
      'open-publish-panel': (p) => openPublishPanel(p)
    }
  })
}

const openPublishPanel = (page) => {
  slidePanel.value.open({
    component: PublishToWpPanel,
    props: { websitePageId: page.id, websiteId: website.value.id },
    events: {
      published: () => pagesList.value?.refresh()
    }
  })
}

const onSitemapImported = () => {
  fetchWebsite()
  pagesList.value?.refresh()
}

const openImportSitemap = () => {
  slidePanel.value.open({
    component: SitemapImportForm,
    props: { websiteId: website.value.id, websiteName: website.value.name },
    events: { imported: onSitemapImported }
  })
}

const loadAnalyticsOptions = async(type) => {
  loadingGoogleAccounts.value = true
  try {
    const accountsResp = await getGoogleAccounts()
    const accounts = accountsResp.data || []

    const fetches = accounts.map((account) => {
      const fetcher = type === 'ga' ?
        getAnalyticsProperties(account.id) :
        getSearchConsoleSites(account.id)
      return fetcher.then((resp) => {
        const items = resp.data || []
        return items.map((item) => ({
          id: item.id,
          label: `${item.display_name || item.site_url || item.name} (${account.email})`,
          accountEmail: account.email
        }))
      })
    })

    const results = await Promise.all(fetches)
    return results.flat()
  } finally {
    loadingGoogleAccounts.value = false
  }
}

const onAnalyticsSaved = () => {
  slidePanel.value.close()
  fetchWebsite()
}

const openGaSetup = async() => {
  const options = await loadAnalyticsOptions('ga')
  slidePanel.value.open({
    component: AnalyticsSetupForm,
    props: { type: 'ga', options, websiteId: website.value.id, websiteName: website.value.name },
    events: { saved: onAnalyticsSaved }
  })
}

const openGscSetup = async() => {
  const options = await loadAnalyticsOptions('gsc')
  slidePanel.value.open({
    component: AnalyticsSetupForm,
    props: { type: 'gsc', options, websiteId: website.value.id, websiteName: website.value.name },
    events: { saved: onAnalyticsSaved }
  })
}

const onBulkAuditCompleted = () => {
  pagesList.value?.refresh()
}

const computePriorityScores = async() => {
  computingPriority.value = true
  try {
    await computePriorityScoresApi(website.value.id)
    // Poll for completion — refresh pages after a delay to pick up scores
    setTimeout(() => {
      pagesList.value?.refresh()
      computingPriority.value = false
    }, 5000)
  } catch (e) {
    console.error('Failed to start priority computation:', e)
    computingPriority.value = false
  }
}

const handleExtractAll = async() => {
  try {
    extractingAll.value = true
    await extractAllPages(website.value.id)
    // eslint-disable-next-line no-alert
    alert('Content extraction started in background. Pages will update as they are processed.')
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error starting extraction:', error)
  } finally {
    extractingAll.value = false
  }
}

// Page Builder actions
const handleApproveBuild = async (build) => {
  try {
    await approvePageBuild(build.id)
    buildsList.value?.fetchBuilds()
  } catch (e) {
    console.error('Failed to approve build', e)
  }
}

const handleRejectBuild = async (build) => {
  const notes = prompt('Rejection notes:')
  if (notes === null) return
  try {
    await rejectPageBuild(build.id, notes)
    buildsList.value?.fetchBuilds()
  } catch (e) {
    console.error('Failed to reject build', e)
  }
}

const handlePublishBuild = async (build) => {
  try {
    await publishPageBuild(build.id)
    buildsList.value?.fetchBuilds()
  } catch (e) {
    console.error('Failed to publish build', e)
  }
}

const onBuildUpdated = () => {
  buildsList.value?.fetchBuilds()
}

const onBulkCreated = () => {
  showBulkCreate.value = false
  buildsList.value?.fetchBuilds()
}

onMounted(() => {
  hideLoadingSpinner()
  fetchWebsite()
})
</script>
